/**
 * Groups API — all backend calls for the Resident Groups tab.
 *
 * Each function maps directly to a V1 endpoint / stored procedure
 * but uses the new V2 api client for cleaner code.
 *
 * Endpoints & payloads are identical to V1 for full backend compatibility.
 */

import { apiFetch, apiTransaction } from '../../../../services/api.js';

// ---------------------------------------------------------------------------
// READ operations
// ---------------------------------------------------------------------------

/**
 * Fetch all resident groups with their members for a campus.
 * V1 procedure: sp_dev_getresidentgroupandresidentbycampusid
 *
 * @param {number} campusId
 * @param {boolean} [readOnly=true]
 * @returns {Promise<Array>}
 */
export function fetchGroups(campusId, readOnly = true) {
  return apiFetch({
    procedure: 'sp_dev_getresidentgroupandresidentbycampusid',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    payload: {
      campus_id: campusId,
      category_filter: '',
      entity_type_filter: '',
      entity_filter: '',
    },
    readOnly,
  });
}

/**
 * Fetch group category options.
 * V1 table: att_resident_group_category (fetchall)
 *
 * @returns {Promise<Array>}
 */
export function fetchGroupCategories() {
  return apiFetch({
    procedure: 'fetchall',
    reqType: 'direct',
    table: 'att_resident_group_category',
    readOnly: true,
  });
}

// ---------------------------------------------------------------------------
// WRITE operations
// ---------------------------------------------------------------------------

/**
 * Create a new resident group.
 * V1 TransType: resident_group_add
 *
 * @param {object} data — { resident_group_name, resident_description, resident_group_category_id, resident_ids }
 * @param {object} session — { campusId, userId, clientId }
 * @returns {Promise<[boolean, string?]>}
 */
export function addGroup(data, session) {
  const payload = [{
    campus_id: session.campusId,
    resident_group_name: data.resident_group_name,
    resident_description: data.resident_description || '',
    resident_ids: data.resident_ids || [],
    resident_group_category_id: parseInt(data.resident_group_category_id),
  }];

  return apiTransaction({
    transType: 'resident_group_add',
    payload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Edit an existing resident group.
 * V1 TransType: resident_group_edit
 *
 * @param {object} data — Updated group data
 * @param {object} oldData — Original group data (for change tracking)
 * @param {object} session
 * @returns {Promise<[boolean, string?]>}
 */
export function editGroup(data, oldData, session) {
  const payload = [{
    resident_group_name: data.resident_group_name,
    resident_group_id: parseInt(data.resident_group_id),
    resident_group_category_id: parseInt(data.resident_group_category_id),
    resident_description: data.resident_description || '',
    resident_ids: data.resident_ids || [],
    campus_id: session.campusId,
  }];

  const oldPayload = [{
    resident_group_name: oldData.resident_group_name,
    resident_group_id: parseInt(oldData.resident_group_id),
    resident_group_category_id: oldData.resident_group_category_id,
    resident_description: oldData.resident_description || '',
    resident_ids: oldData.resident_ids || [],
    campus_id: session.campusId,
  }];

  return apiTransaction({
    transType: 'resident_group_edit',
    payload,
    oldPayload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Delete one or more resident groups.
 * V1 TransType: resident_group_delete
 *
 * @param {Array<number>} groupIds
 * @param {object} session
 * @returns {Promise<[boolean, string?]>}
 */
export function deleteGroups(groupIds, session) {
  const payload = groupIds.map((id) => ({
    resident_group_id: parseInt(id),
  }));

  return apiTransaction({
    transType: 'resident_group_delete',
    payload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}
